/**
 * Gated HITL bridge for `fill_cavities` — fills a structure's enclosed 3D
 * holes IN PLACE, with the same result as the toolbar's Fill cavities button
 * (same engine call; the structure's contours are replaced with one undo
 * step; approved structures refused).
 *
 * Flow: parse the model's tool input → resolve the target with the shared
 * resolver (point / empty / ambiguous / not-found guards) → refuse approved
 * structures → compute the filled geometry ONCE → a
 * {@link FillCavitiesProposal} the card renders (hover shows the filled
 * result). Nothing changes until the clinician Applies; Apply writes the
 * ALREADY-computed geometry through the shared in-place gate
 * ({@link gateInPlaceProposal}), which refuses a stale preview.
 * Pure/React-free; the engine is injected.
 */
import {
    asRecord,
    failedPlan,
    gateInPlaceProposal,
    mutatingPreamble,
    notModifiableReason,
    reasonOf,
    resolveFailureToPlan,
    resolveOneStructure,
    toListed,
    type ComputedGeometry,
    type InPlaceEditDeps,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
    type StructureFacts,
} from '../core';
import type { FillCavitiesInput, InPlaceEditOutcome } from '../../contract';

export interface FillCavitiesDeps extends InPlaceEditDeps, PendingFlag {
    readonly structures: ReadonlyArray<StructureFacts>;
    readonly geometry: InPlaceEditDeps['geometry'] & {
        applyFillCavities(roiNumber: number): Promise<ComputedGeometry>;
    };
}

export interface FillCavitiesProposal {
    readonly toolCallId: string;
    readonly target: {
        readonly roiNumber: number;
        readonly name: string;
        readonly currentVolumeCm3: number;
    };
    /** Volume added by filling (after minus before, cm³). */
    readonly volumeDeltaCm3: number;
    apply(): Promise<InPlaceEditOutcome>;
    reject(): InPlaceEditOutcome;
    /** Show a translucent overlay of the filled result (card hover). */
    showPreview(): void;
    /** Clear the hover overlay. Never deletes the previewed geometry. */
    hidePreview(): void;
}

export type FillCavitiesPlan = MutatingPlan<FillCavitiesProposal>;

/** Defensively read the model-supplied (untyped) tool input. */
function readInput(input: unknown): FillCavitiesInput | undefined {
    const o = asRecord(input);
    if (o === undefined || typeof o.roi !== 'string') {
        return undefined;
    }
    return { roi: o.roi };
}

export async function planFillCavitiesToolCall(
    call: MutatingToolCall,
    deps: FillCavitiesDeps
): Promise<FillCavitiesPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }

    const parsed = readInput(call.input);
    if (parsed === undefined) {
        return failedPlan(call.toolCallId, 'invalid fill_cavities input');
    }

    const resolved = resolveOneStructure(
        parsed.roi,
        deps.structures,
        deps.structures.map(toListed),
        'cavity fill',
        'fill'
    );
    if (resolved.kind !== 'source') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }
    const { roiNumber, name, volumeCm3: volumeBeforeCm3 } = resolved.source;
    if (!deps.canModify(roiNumber)) {
        return failedPlan(call.toolCallId, notModifiableReason(name));
    }

    // Captured BEFORE computing, so an edit racing the computation is caught.
    const target = {
        roiNumber,
        name,
        volumeBeforeCm3,
        plannedVersion: deps.editVersion(roiNumber),
        plannedContextId: deps.currentContextId(),
    };

    let computed: ComputedGeometry;
    try {
        computed = await deps.geometry.applyFillCavities(roiNumber);
    } catch (err) {
        return failedPlan(call.toolCallId, reasonOf(err));
    }
    if (!(computed.resultVolumeCm3 > 0)) {
        deps.geometry.releaseComputed?.(computed.handle);
        return failedPlan(
            call.toolCallId,
            'the resulting structure would be empty'
        );
    }

    const proposal: FillCavitiesProposal = {
        toolCallId: call.toolCallId,
        target: { roiNumber, name, currentVolumeCm3: volumeBeforeCm3 },
        volumeDeltaCm3: computed.resultVolumeCm3 - volumeBeforeCm3,
        ...gateInPlaceProposal(computed, target, deps),
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
